import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiUser, FiBell, FiUsers, FiLogOut, FiServer } from 'react-icons/fi';
import ShieldCheckIcon from '../ShieldCheckIcon';
import api from '../../../api/api';
import { IGlobalState } from '../../../model/IGlobalState';
import { Avatar, displayName } from '../Header';

// left-hand card shared by every account page
const AccountNav: React.FC = () => {
    const user = useSelector((state: IGlobalState) => state.user);
    const [clientCount, setClientCount] = useState<number | null>(null);

    useEffect(() => {
        api().getClientDashboardResult().then(r => setClientCount(r.stocks.length)).catch(() => setClientCount(null));
    }, []);

    // tablet / mobile: the nav is a sideways-scrolling tab row and each page mounts its own copy, so bring the
    // active tab into view (centred) instead of leaving the row at the first tab; no-op on desktop (nothing to scroll)
    const navRef = useRef<HTMLElement>(null);
    const location = useLocation();
    useEffect(() => {
        const nav = navRef.current;
        const active = nav?.querySelector<HTMLElement>('a.active');
        if (!nav || !active || nav.scrollWidth <= nav.clientWidth) return;
        nav.scrollLeft = active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2;
    }, [location.pathname, clientCount]);

    const logout = async () => {
        await api().logout();
        window.location.href = '/';
    };

    return <nav className="account-nav" ref={navRef}>
        <div className="me">
            <Avatar user={user} />
            <div className="text">
                <strong>{displayName(user)}</strong>
                {clientCount !== null && <span>Manages {clientCount} {clientCount === 1 ? 'client' : 'clients'}</span>}
            </div>
        </div>
        <NavLink to="/account" exact activeClassName="active"><FiUser /> My Profile</NavLink>
        <NavLink to="/account/security" activeClassName="active"><ShieldCheckIcon /> Account &amp; Security</NavLink>
        <NavLink to="/account/notifications" activeClassName="active"><FiBell /> Notification Preferences</NavLink>
        <NavLink to="/account/mcp" activeClassName="active"><FiServer /> MCP Server</NavLink>
        {/* spec: only for people who manage more than one company */}
        {clientCount !== null && clientCount > 1 && <NavLink to="/account/clients" activeClassName="active"><FiUsers /> My Clients</NavLink>}
        <hr />
        <button type="button" className="sign-out" onClick={logout}><FiLogOut /> Sign Out</button>
    </nav>;
};

export default AccountNav;
